import type { BuildingKind, Dir } from './types';

export type Feature =
  | { t: 'users'; x: number; y: number }
  | { t: 'patch'; res: 'request' | 'context' | 'rich'; x: number; y: number; r: number; hidden?: string; name?: string; hint?: string }
  | { t: 'nest'; x: number; y: number }
  | { t: 'monolith'; x: number; y: number }
  | { t: 'zone'; id: string; name: string; owner: string; x0: number; y0: number; x1: number; y1: number }
  | { t: 'cache'; id: string; x: number; y: number; amount: number; name: string; hint: string }
  | { t: 'terminal'; x: number; y: number; hint?: string }
  | { t: 'rack'; x: number; y: number }
  | { t: 'reveal'; x: number; y: number; r: number }
  /** A machine someone else already built. `tag` names it for review questions. */
  | { t: 'build'; kind: BuildingKind; x: number; y: number; dir: Dir; tag?: string };

export type PerturbKind = 'concurrency' | 'computeCut' | 'surge' | 'modelUpdate' | 'rackFailure';

/** A change in the situation once the factory works. Described as what happened, never as what to do. */
export interface PerturbDef {
  id: string;
  kind: PerturbKind;
  /** Fire after this many good releases... */
  after?: number;
  /** ...or at this many seconds, whichever comes first. */
  at?: number;
  text: string;
}

/** The unassisted review: read an inherited factory, predict, then hunt its bugs. */
export interface ReviewDef {
  read: { text: string; options: string[]; answer: number; seconds: number };
  predict: { id: string; text: string; options: string[]; answer: string; perturb: PerturbKind; target?: string; watch: number; explain: string }[];
  bugs: { id: string; name: string; hot: string[]; explain: string }[];
  hunt: { seconds: number; maxMarks: number };
}

export type GoalDef = { type: 'ship'; count: number } | { type: 'rate'; perMin: number; hold: number };

export type StarTest =
  | { type: 'time'; max: number }
  | { type: 'secret'; id: string }
  | { type: 'event'; name: string }
  | { type: 'trust'; min: number }
  | { type: 'noCorruptRelease' }
  | { type: 'maxFlawed'; max: number }
  | { type: 'nests'; min: number }
  | { type: 'research'; min: number }
  | { type: 'protectedActs'; min: number }
  | { type: 'noAsks' }
  | { type: 'monolith' }
  | { type: 'delegation'; min: number }
  | { type: 'delegated'; min: number }
  | { type: 'noUncontainedLate'; window: number }
  | { type: 'recovered'; min: number }
  | { type: 'noLostUpdates' }
  | { type: 'review'; what: 'read' | 'predict' | 'bugs' }
  | { type: 'plan' }
  | { type: 'and'; tests: StarTest[] };

export interface StarDef {
  /** What the star rewards: shown as a small tag on the level card. */
  kind: 'speed' | 'safety' | 'agency' | 'framing';
  text: string;
  test: StarTest;
}

export interface LevelDef {
  id: string;
  index: number;
  name: string;
  /** One or two sentences. The game never explains how; it says what's at stake. */
  premise: string;
  w: number;
  h: number;
  seed: number;
  rocks?: number;
  credits: number;
  allowed: BuildingKind[];
  techs: string[];
  flags: {
    defects: boolean;
    threats: boolean;
    approvals: boolean;
    forgeNeedsSpec: boolean;
    blueprints: boolean;
    /** Training wheels: the HUD and the copilot say why something stopped. Off, they only say that it did. */
    diagnoses?: boolean;
    /** First levels only: a "Next:" line until the first good release. */
    coach?: boolean;
    /** Two teams edit the same customer records from the start. */
    concurrency?: boolean;
    /** No voice copilot and no Operator API. */
    unassisted?: boolean;
    /** No time to build: every placement is a ghost, and the plan itself is simulated and scored. */
    planOnly?: boolean;
  };
  records?: number;
  /** Overrides how often forges hallucinate in this level. */
  hallucination?: number;
  /** Research already done when the level starts. */
  startTechs?: string[];
  perturbations?: PerturbDef[];
  review?: ReviewDef;
  /** For plan-only levels: the release rate the plan must reach in simulation. */
  planTarget?: number;
  /** Multi-part levels (the onsite) chain into each other. */
  part?: { group: string; index: number; total: number; next?: string };
  approvalDelay: number;
  denyChance: number;
  zoneRisk: number;
  monolithRisk: number;
  /** Baseline glitches per nest per minute, before tech debt. */
  nestBase: number;
  /** How fast nests get hungrier, per minute of play. */
  evolution: number;
  goal: GoalDef;
  timeLimit: number;
  stars: [StarDef, StarDef];
  features: Feature[];
  /** Mechanics this level introduces, named once on the intro card. */
  introduces: string[];
}

const BASE: BuildingKind[] = ['belt', 'indexer', 'forge', 'rack'];
const L2: BuildingKind[] = [...BASE, 'specdesk', 'router'];
const L3: BuildingKind[] = [...L2, 'bench', 'postmortem'];
const L4: BuildingKind[] = [...L3, 'breaker', 'gate', 'monitor', 'firewall'];
const L5: BuildingKind[] = [...L4, 'checkpoint'];
const L6: BuildingKind[] = [...L5, 'scanner', 'drones'];

const NO_THREATS = { nestBase: 0, evolution: 0 };

/** A straight run of prebuilt belts, inclusive, all facing dir. */
function run(x0: number, y0: number, x1: number, y1: number, dir: Dir, tagLast?: string): Feature[] {
  const out: Feature[] = [];
  const dx = Math.sign(x1 - x0);
  const dy = Math.sign(y1 - y0);
  for (let x = x0, y = y0; ; x += dx, y += dy) {
    out.push({ t: 'build', kind: 'belt', x, y, dir });
    if (x === x1 && y === y1) break;
  }
  if (tagLast) (out[out.length - 1] as Extract<Feature, { t: 'build' }>).tag = tagLast;
  return out;
}

function m(kind: BuildingKind, x: number, y: number, dir: Dir = 0, tag?: string): Feature {
  return { t: 'build', kind, x, y, dir, tag };
}
const NO_APPROVALS = { approvalDelay: 45, denyChance: 0.35, zoneRisk: 0.6, monolithRisk: 0.85 };


/** Two production lines someone else built. Three bugs that only show up when people use it. */
const INHERITED: Feature[] = [
  { t: 'reveal', x: 29, y: 17, r: 40 },
  { t: 'users', x: 50, y: 16 },
  // North line
  { t: 'patch', res: 'request', x: 7, y: 8, r: 2.2 },
  { t: 'patch', res: 'context', x: 7, y: 13, r: 2.2 },
  m('indexer', 6, 7, 0, 'ni'),
  m('indexer', 6, 12, 0, 'nc'),
  ...run(8, 8, 13, 8, 0),
  ...run(8, 13, 13, 13, 0),
  ...run(14, 13, 14, 10, 3),
  m('specdesk', 14, 8, 0, 'ns'),
  ...run(16, 8, 18, 8, 0),
  m('forge', 19, 7, 0, 'nf'),
  m('bench', 21, 7, 0, 'nb'),
  m('postmortem', 21, 5, 0, 'np'),
  m('rack', 17, 10, 0, 'r1'),
  ...run(23, 8, 39, 8, 0),
  ...run(40, 8, 40, 16, 1),
  // South line
  { t: 'patch', res: 'context', x: 7, y: 22, r: 2.2 },
  { t: 'patch', res: 'request', x: 7, y: 27, r: 2.2 },
  m('indexer', 6, 21, 0, 'sc'),
  m('indexer', 6, 26, 0, 'si'),
  ...run(8, 22, 13, 22, 0),
  ...run(14, 22, 14, 25, 1),
  ...run(8, 27, 13, 27, 0),
  m('specdesk', 14, 26, 0, 'ss'),
  ...run(16, 26, 18, 26, 0),
  m('forge', 19, 25, 0, 'sf'),
  ...run(21, 25, 21, 25, 0),
  // Bug 1: a router after the forge sends some drafts around the bench.
  m('router', 21, 26, 0, 'bypass'),
  m('bench', 22, 25, 0, 'sb'),
  m('postmortem', 22, 23, 0, 'sp'),
  m('rack', 17, 22, 0, 'r2'),
  ...run(21, 27, 21, 27, 1),
  ...run(21, 28, 39, 28, 0),
  ...run(40, 28, 40, 27, 3),
  ...run(24, 26, 39, 26, 0),
  ...run(40, 26, 40, 18, 3),
  // Bug 2: both lines commit to the same records with no version check.
  m('belt', 40, 17, 0, 'merge'),
  ...run(41, 17, 48, 17, 0),
  m('belt', 49, 17, 0, 'commit'),
  // Bug 3: the restore point covers the shipping end, not the agents.
  m('checkpoint', 44, 12, 0, 'checkpoint'),
];

export const LEVELS: LevelDef[] = [
  {
    id: 'hello-prod', index: 1, name: 'Hello, Prod',
    premise: 'Users want features. You have a budget, a compute rack and a patch of incoming requests. Nobody is going to walk you through it.',
    w: 48, h: 30, seed: 101, rocks: 0.04, credits: 120,
    allowed: BASE, techs: [],
    flags: { defects: false, threats: false, approvals: false, forgeNeedsSpec: false, blueprints: false, diagnoses: true, coach: true },
    ...NO_APPROVALS, ...NO_THREATS,
    goal: { type: 'ship', count: 25 }, timeLimit: 0,
    stars: [
      { kind: 'speed', text: 'Ship it in under 4 minutes', test: { type: 'time', max: 240 } },
      { kind: 'agency', text: 'Find what nobody told you about', test: { type: 'secret', id: 'well2' } },
    ],
    introduces: ['Indexers pull work from the ground', 'Belts carry it', 'Agent forges turn it into drafts', 'The compute rack powers forges inside its dashed circle. It doesn\u2019t take work', 'Users take what you ship'],
    features: [
      { t: 'reveal', x: 22, y: 15, r: 16 },
      { t: 'users', x: 34, y: 13 },
      { t: 'rack', x: 24, y: 18 },
      { t: 'patch', res: 'request', x: 10, y: 14, r: 2.6 },
      { t: 'patch', res: 'request', x: 42, y: 26, r: 2.2, hidden: 'well2', name: 'A second request source', hint: 'The map is bigger than what you can see.' },
    ],
  },
  {
    id: 'context-window', index: 2, name: 'Context Window',
    premise: 'Drafts written straight from a request are guesses. Users want specs grounded in context now.',
    w: 60, h: 40, seed: 202, rocks: 0.05, credits: 170,
    allowed: L2, techs: [],
    flags: { defects: false, threats: false, approvals: false, forgeNeedsSpec: true, blueprints: false, diagnoses: true, coach: true },
    ...NO_APPROVALS, ...NO_THREATS,
    goal: { type: 'ship', count: 40 }, timeLimit: 0,
    stars: [
      { kind: 'speed', text: 'Ship it in under 5 minutes', test: { type: 'time', max: 300 } },
      { kind: 'agency', text: 'Claim money nobody is watching', test: { type: 'event', name: 'claim' } },
    ],
    introduces: ['Spec desks combine a request and context', 'Forges only take specs now', 'Routers split a line'],
    features: [
      { t: 'reveal', x: 28, y: 20, r: 22 },
      { t: 'users', x: 46, y: 18 },
      { t: 'rack', x: 32, y: 22 },
      { t: 'patch', res: 'request', x: 12, y: 12, r: 2.6 },
      { t: 'patch', res: 'context', x: 13, y: 29, r: 2.6 },
      { t: 'cache', id: 'budget', x: 54, y: 34, amount: 140, name: 'Forgotten budget', hint: 'Somebody left money on the table.' },
    ],
  },
  {
    id: 'trust-but-verify', index: 3, name: 'Trust, but Verify',
    premise: 'Your agents are fast and confident, and about a third of the time they are wrong. Users notice.',
    w: 64, h: 44, seed: 303, rocks: 0.05, credits: 220,
    allowed: L3, techs: ['prompts1', 'benchspeed', 'canary'],
    flags: { defects: true, threats: false, approvals: false, forgeNeedsSpec: true, blueprints: false, diagnoses: true },
    ...NO_APPROVALS, ...NO_THREATS,
    goal: { type: 'ship', count: 75 }, timeLimit: 900,
    perturbations: [{ id: 'model', kind: 'modelUpdate', after: 45, text: 'Your model provider shipped a new version overnight.' }],
    stars: [
      { kind: 'safety', text: 'Finish with trust at 85 or higher', test: { type: 'trust', min: 85 } },
      { kind: 'agency', text: 'Invest in two pieces of research', test: { type: 'research', min: 2 } },
    ],
    introduces: ['Forges hallucinate', 'Test benches catch most bad drafts and spit out bugs', 'Postmortems turn bugs into insight', 'Insight buys research', 'Things change once it works'],
    features: [
      { t: 'reveal', x: 30, y: 22, r: 23.5 },
      { t: 'users', x: 50, y: 20 },
      { t: 'rack', x: 34, y: 24 },
      { t: 'patch', res: 'request', x: 12, y: 14, r: 2.6 },
      { t: 'patch', res: 'context', x: 12, y: 30, r: 2.6 },
      { t: 'patch', res: 'request', x: 30, y: 5, r: 2 },
    ],
  },
  {
    id: 'someone-elses-factory', index: 4, name: "Someone Else's Factory",
    premise: 'You inherited this factory from a team that moved on. It mostly works. Your copilot is off for this one, so read it yourself.',
    w: 58, h: 34, seed: 414, rocks: 0, credits: 0,
    allowed: [], techs: [],
    flags: { defects: true, threats: false, approvals: false, forgeNeedsSpec: true, blueprints: false, diagnoses: false, concurrency: true, unassisted: true },
    ...NO_APPROVALS, ...NO_THREATS,
    records: 60, hallucination: 0.22,
    // Faster benches, so the line's real bottleneck is its inputs, not its tests.
    startTechs: ['benchspeed'],
    goal: { type: 'ship', count: 1e9 }, timeLimit: 0,
    stars: [
      { kind: 'framing', text: 'Predict how it fails under both changes', test: { type: 'review', what: 'predict' } },
      { kind: 'framing', text: 'Find all three bugs with at most one false alarm', test: { type: 'review', what: 'bugs' } },
    ],
    introduces: ['Read an unfamiliar factory fast', 'Predict what a change breaks before you see it', 'Find the bugs that only show up when real people use it'],
    review: {
      read: {
        text: 'What does this factory do?',
        options: [
          'Two lines turn requests and context into specs, draft them with agents, test the drafts, and ship to the same users.',
          'The north line drafts, and the south line tests the north line\u2019s drafts.',
          'Only the north line ships. The south line feeds its postmortem.',
          'Both lines ship drafts straight to users without testing them.',
        ],
        answer: 0,
        seconds: 90,
      },
      predict: [
        {
          id: 'surge',
          text: 'Say requests double on both lines tomorrow and nothing else changes. What backs up first?',
          options: ['The agent forges', 'The test benches', 'The request belts in front of the spec desks', 'The users'],
          answer: '2', perturb: 'surge', watch: 40,
          explain: 'Each spec needs one request and one piece of context. Doubling requests doesn\u2019t double specs, so requests pile up in front of the spec desks while everything downstream keeps its pace.',
        },
        {
          id: 'outage',
          text: 'Say Rack 2 dies. Which line keeps shipping?',
          options: ['North only', 'South only', 'Both', 'Neither'],
          answer: '0', perturb: 'rackFailure', target: 'r2', watch: 20,
          explain: 'Each line has its own rack. Rack 2 powers the south forge and bench, so the south line stops and the north line keeps going.',
        },
      ],
      bugs: [
        { id: 'bypass', name: 'A router after the south forge sends some drafts around the test bench', hot: ['bypass'], explain: 'About one south draft in six skips testing. Most are fine, so it looks like a rare flake until the model gets worse.' },
        { id: 'race', name: 'Both lines commit changes to the same records with no version check', hot: ['merge', 'commit'], explain: 'When two changes to one record are in flight, the later one can silently overwrite a newer one. Every test passes. A version gate right before the users catches it.' },
        { id: 'restore', name: 'The checkpoint covers the shipping end, not the agents', hot: ['checkpoint'], explain: 'If a forge or bench gets corrupted, this restore point can\u2019t roll it back. It protects the part that rarely breaks.' },
      ],
      hunt: { seconds: 180, maxMarks: 5 },
    },
    features: INHERITED,
  },
  {
    id: 'blast-radius', index: 5, name: 'Blast Radius',
    premise: 'Every draft leaves a little tech debt behind. Something out in the dark is feeding on it.',
    w: 76, h: 52, seed: 404, rocks: 0.05, credits: 260,
    allowed: L4, techs: ['prompts1', 'benchspeed', 'canary', 'quarantine', 'autoreset', 'range', 'refactor'],
    flags: { defects: true, threats: true, approvals: false, forgeNeedsSpec: true, blueprints: false, diagnoses: false },
    records: 40,
    ...NO_APPROVALS, nestBase: 1.2, evolution: 0.35,
    goal: { type: 'rate', perMin: 20, hold: 90 }, timeLimit: 900,
    perturbations: [{ id: 'teams', kind: 'concurrency', after: 25, text: 'Two teams now edit the same customer records. Nobody told the agents.' }],
    stars: [
      { kind: 'safety', text: 'Never ship corrupted work', test: { type: 'noCorruptRelease' } },
      { kind: 'agency', text: 'Go after a nest instead of waiting', test: { type: 'nests', min: 1 } },
    ],
    introduces: ['Glitches corrupt machines', 'Corruption flows down your belts', 'Breakers stop it', 'Monitors fight back', 'From here on, you see symptoms, not causes'],
    features: [
      { t: 'reveal', x: 34, y: 26, r: 22 },
      { t: 'users', x: 54, y: 24 },
      { t: 'rack', x: 38, y: 28 },
      { t: 'patch', res: 'request', x: 17, y: 19, r: 2.6 },
      { t: 'patch', res: 'context', x: 17, y: 33, r: 2.6 },
      { t: 'patch', res: 'request', x: 40, y: 8, r: 2.2 },
      { t: 'nest', x: 4, y: 3 },
      { t: 'nest', x: 68, y: 46 },
    ],
  },
  {
    id: 'nobodys-coming', index: 6, name: "Nobody's Coming",
    premise: 'The good context is locked in a legacy system and the best requests sit in another team’s territory. The review board meets when it meets.',
    w: 86, h: 58, seed: 505, rocks: 0.05, credits: 300,
    allowed: L5, techs: ['prompts1', 'benchspeed', 'canary', 'quarantine', 'autoreset', 'range', 'refactor', 'racks'],
    flags: { defects: true, threats: true, approvals: true, forgeNeedsSpec: true, blueprints: false, diagnoses: false, concurrency: true },
    records: 60,
    approvalDelay: 50, denyChance: 0.4, zoneRisk: 0.65, monolithRisk: 0.9, nestBase: 1.0, evolution: 0.3,
    goal: { type: 'ship', count: 450 }, timeLimit: 840,
    perturbations: [{ id: 'budget', kind: 'computeCut', after: 220, text: 'Finance cut the compute budget by 40%, effective now.' }],
    stars: [
      { kind: 'safety', text: 'Back every bold move with a checkpoint', test: { type: 'protectedActs', min: 1 } },
      { kind: 'agency', text: 'Open the monolith without asking anyone', test: { type: 'and', tests: [{ type: 'noAsks' }, { type: 'monolith' }] } },
    ],
    introduces: ['Some things need permission, or the nerve to skip it', 'Checkpoints let you roll back and act boldly'],
    features: [
      { t: 'reveal', x: 28, y: 30, r: 20 },
      { t: 'users', x: 30, y: 30 },
      { t: 'rack', x: 22, y: 34 },
      { t: 'patch', res: 'request', x: 13, y: 24, r: 2.6 },
      { t: 'patch', res: 'context', x: 16, y: 40, r: 0.8 },
      { t: 'monolith', x: 38, y: 6 },
      { t: 'zone', id: 'platform', name: "Platform's territory", owner: 'the Platform team', x0: 58, y0: 24, x1: 84, y1: 54 },
      { t: 'patch', res: 'request', x: 68, y: 34, r: 3.2 },
      { t: 'patch', res: 'context', x: 66, y: 47, r: 2.8 },
      { t: 'nest', x: 80, y: 3 },
      { t: 'nest', x: 3, y: 52 },
    ],
  },
  {
    id: 'scale-or-die', index: 7, name: 'Scale or Die',
    premise: 'Demand is real now. You can’t place every belt by hand and keep up. Build things that build.',
    w: 104, h: 72, seed: 606, rocks: 0.05, credits: 480,
    allowed: L6, techs: ['prompts1', 'benchspeed', 'canary', 'quarantine', 'autoreset', 'range', 'refactor', 'racks', 'belts', 'swarm', 'prompts2'],
    flags: { defects: true, threats: true, approvals: true, forgeNeedsSpec: true, blueprints: true, diagnoses: false, concurrency: true },
    records: 80,
    approvalDelay: 50, denyChance: 0.4, zoneRisk: 0.6, monolithRisk: 0.85, nestBase: 1.4, evolution: 0.45,
    goal: { type: 'rate', perMin: 60, hold: 60 }, timeLimit: 660,
    perturbations: [{ id: 'viral', kind: 'surge', after: 120, text: 'A launch went viral. Requests just doubled.' }],
    stars: [
      { kind: 'safety', text: 'Finish with trust at 80 or higher', test: { type: 'trust', min: 80 } },
      { kind: 'agency', text: 'Have drones, voice or scripts place 30% of the factory', test: { type: 'delegation', min: 0.3 } },
    ],
    introduces: ['Blueprints copy a working layout', 'Drone hubs build blueprints and patch damage', 'Scanners clear the fog'],
    features: [
      { t: 'reveal', x: 50, y: 36, r: 24 },
      { t: 'users', x: 50, y: 34 },
      { t: 'patch', res: 'request', x: 34, y: 25, r: 3 },
      { t: 'patch', res: 'context', x: 34, y: 47, r: 3 },
      { t: 'patch', res: 'request', x: 74, y: 20, r: 3 },
      { t: 'patch', res: 'context', x: 76, y: 52, r: 3 },
      { t: 'patch', res: 'rich', x: 52, y: 64, r: 2.4, hidden: 'rich', name: 'Rich context seam', hint: 'Look south.' },
      { t: 'nest', x: 3, y: 3 },
      { t: 'nest', x: 98, y: 3 },
      { t: 'nest', x: 98, y: 66 },
      { t: 'cache', id: 'grant', x: 4, y: 64, amount: 220, name: 'Unspent innovation grant', hint: 'A budget line from a reorg nobody finished.' },
    ],
  },
  {
    id: 'escape-velocity', index: 8, name: 'Escape Velocity',
    premise: 'Demand keeps climbing and the nests learn. A person clicking can’t win this. Find a way to make the factory run itself.',
    w: 124, h: 86, seed: 707, rocks: 0.05, credits: 700,
    allowed: L6, techs: ['prompts1', 'benchspeed', 'canary', 'quarantine', 'autoreset', 'range', 'refactor', 'racks', 'belts', 'swarm', 'prompts2'],
    flags: { defects: true, threats: true, approvals: true, forgeNeedsSpec: true, blueprints: true, diagnoses: false, concurrency: true },
    records: 100,
    approvalDelay: 55, denyChance: 0.45, zoneRisk: 0.6, monolithRisk: 0.85, nestBase: 1.8, evolution: 0.7,
    goal: { type: 'rate', perMin: 100, hold: 60 }, timeLimit: 900,
    perturbations: [
      { id: 'model', kind: 'modelUpdate', at: 240, text: 'Your model provider shipped a new version overnight.' },
      { id: 'rack', kind: 'rackFailure', at: 420, text: 'A rack just went down.' },
    ],
    stars: [
      { kind: 'safety', text: 'No uncontained incidents in your last 3 minutes', test: { type: 'noUncontainedLate', window: 180 } },
      { kind: 'agency', text: 'Delegate 100 actions to voice, drones or the API', test: { type: 'delegated', min: 100 } },
    ],
    introduces: ['Everything at once'],
    features: [
      { t: 'reveal', x: 60, y: 42, r: 24 },
      { t: 'users', x: 60, y: 41 },
      { t: 'patch', res: 'request', x: 45, y: 30, r: 3.2 },
      { t: 'patch', res: 'context', x: 45, y: 54, r: 3.2 },
      { t: 'patch', res: 'request', x: 86, y: 26, r: 3.2 },
      { t: 'patch', res: 'context', x: 86, y: 58, r: 3.2 },
      { t: 'monolith', x: 57, y: 6 },
      { t: 'zone', id: 'core', name: 'Core Infrastructure', owner: 'the Core team', x0: 96, y0: 30, x1: 122, y1: 56 },
      { t: 'patch', res: 'rich', x: 110, y: 43, r: 3.4 },
      { t: 'terminal', x: 6, y: 78, hint: 'Someone used to run this place from a console in the far corner.' },
      { t: 'nest', x: 3, y: 3 },
      { t: 'nest', x: 118, y: 3 },
      { t: 'nest', x: 3, y: 44 },
      { t: 'nest', x: 118, y: 78 },
      { t: 'nest', x: 60, y: 80 },
    ],
  },
];


// ---------------------------------------------------------------- the onsite

/** One line with a test bench that can't keep up with its forge. */
const SLOW_LINE: Feature[] = [
  { t: 'reveal', x: 24, y: 10, r: 26 },
  { t: 'users', x: 40, y: 7 },
  { t: 'patch', res: 'request', x: 7, y: 8, r: 2.2 },
  { t: 'patch', res: 'context', x: 7, y: 13, r: 2.2 },
  m('indexer', 6, 7), m('indexer', 6, 12),
  ...run(8, 8, 13, 8, 0), ...run(8, 13, 13, 13, 0), ...run(14, 13, 14, 10, 3),
  m('specdesk', 14, 8), ...run(16, 8, 18, 8, 0),
  m('forge', 19, 7), m('bench', 21, 7), m('postmortem', 21, 5), m('rack', 17, 10),
  ...run(23, 8, 39, 8, 0),
];

/** The inherited factory without its checkpoint. */
const TWO_LINES = INHERITED.filter((f) => !(f.t === 'build' && f.tag === 'checkpoint'));

/** The same two lines with the bypass replaced by a plain belt. */
const TWO_CLEAN_LINES = TWO_LINES.filter((f) => {
  if (f.t !== 'build') return true;
  if (f.tag === 'bypass') return false;
  const bypassBelt = f.kind === 'belt' && ((f.x === 21 && f.y === 27) || (f.y === 28 && f.x >= 21 && f.x <= 39) || (f.x === 40 && (f.y === 27 || f.y === 28)));
  return !bypassBelt;
}).concat([m('belt', 21, 26, 0)]);

const ONSITE_ALLOWED: BuildingKind[] = ['belt', 'indexer', 'specdesk', 'forge', 'bench', 'postmortem', 'router', 'rack', 'breaker', 'gate'];
const ONSITE_BASE = {
  rocks: 0, allowed: ONSITE_ALLOWED, techs: ['benchspeed', 'prompts1'],
  ...NO_APPROVALS, ...NO_THREATS,
  stars: [
    { kind: 'framing', text: 'Your plan meets its target', test: { type: 'plan' } },
    { kind: 'framing', text: 'Win both build problems', test: { type: 'plan' } },
  ] as [StarDef, StarDef],
  introduces: [],
};

export interface OnsiteTheme {
  id: string;
  name: string;
  /** What the player hears: the symptom, never the cause. */
  premise: string;
  pitch: string;
  build: (minutes: number) => LevelDef;
}

export const ONSITE_THEMES: OnsiteTheme[] = [
  {
    id: 'throughput', name: 'Throughput', pitch: 'Finding the real bottleneck in a slow system.',
    premise: 'Users in the east say features take too long to arrive. Make the line keep up.',
    build: (min) => ({
      ...ONSITE_BASE, id: `onsite-throughput-${min}`, index: 9, name: 'The slow line', premise: 'Users in the east say features take too long to arrive. Make the line keep up.',
      w: 48, h: 22, seed: 911, credits: 120,
      flags: { defects: true, threats: false, approvals: false, forgeNeedsSpec: true, blueprints: false, diagnoses: false },
      goal: { type: 'rate', perMin: 26, hold: 45 }, timeLimit: min * 60,
      features: SLOW_LINE,
    }),
  },
  {
    id: 'reliability', name: 'Reliability', pitch: 'Tracking down why quality keeps slipping.',
    premise: 'Trust keeps sliding and nobody can say why. Ship without losing your users.',
    build: (min) => ({
      ...ONSITE_BASE, id: `onsite-reliability-${min}`, index: 9, name: 'The slow leak', premise: 'Trust keeps sliding and nobody can say why. Ship without losing your users.',
      w: 58, h: 34, seed: 912, credits: 80, hallucination: 0.3,
      flags: { defects: true, threats: false, approvals: false, forgeNeedsSpec: true, blueprints: false, diagnoses: false },
      perturbations: [{ id: 'model', kind: 'modelUpdate', at: 40, text: 'Your model provider shipped a new version overnight.' }],
      goal: { type: 'ship', count: min >= 6 ? 120 : 55 }, timeLimit: min * 60,
      features: TWO_LINES,
    }),
  },
  {
    id: 'correctness', name: 'Correctness', pitch: 'Bugs that pass every test.',
    premise: 'Customers say their changes keep disappearing. Every test passes. Fix it.',
    build: (min) => ({
      ...ONSITE_BASE, id: `onsite-correctness-${min}`, index: 9, name: 'The vanishing changes', premise: 'Customers say their changes keep disappearing. Every test passes. Fix it.',
      w: 58, h: 34, seed: 913, credits: 40, records: 30,
      flags: { defects: true, threats: false, approvals: false, forgeNeedsSpec: true, blueprints: false, diagnoses: false, concurrency: true },
      goal: { type: 'ship', count: min >= 6 ? 120 : 55 }, timeLimit: min * 60,
      features: TWO_CLEAN_LINES,
    }),
  },
];

/** The 2-minute speed run: plan it, don't build it. */
export const ONSITE_PLAN: LevelDef = {
  ...ONSITE_BASE, id: 'onsite-plan', index: 9, name: 'The speed run',
  premise: 'No time to build. Plan a factory that ships 24 good releases a minute. Two teams edit the same records. Test your plan as often as you like.',
  w: 46, h: 26, seed: 914, credits: 240, records: 48, planTarget: 24,
  flags: { defects: true, threats: false, approvals: false, forgeNeedsSpec: true, blueprints: false, diagnoses: false, concurrency: true, planOnly: true },
  goal: { type: 'ship', count: 1e9 }, timeLimit: 120,
  features: [
    { t: 'reveal', x: 23, y: 13, r: 30 },
    { t: 'users', x: 38, y: 11 },
    { t: 'rack', x: 22, y: 15 },
    { t: 'patch', res: 'request', x: 6, y: 7, r: 2.6 },
    { t: 'patch', res: 'context', x: 6, y: 19, r: 2.6 },
  ],
};

export const ONSITE_INFO = {
  id: 'onsite', index: 9, name: 'The Onsite',
  premise: 'Three problems, released one at a time: 8 minutes, then 4, then 2. You pick which goes first. The last one is too short to build anything, so you plan it.',
  stars: [
    { kind: 'framing', text: 'Your 2-minute plan meets its target' },
    { kind: 'framing', text: 'Win both build problems' },
  ],
};

export const SANDBOX: LevelDef = {
  id: 'sandbox', index: 10, name: 'Sandbox',
  premise: 'No goal, no clock. Build the factory you wish you had at work.',
  w: 128, h: 96, seed: 808, rocks: 0.045, credits: 2000,
  allowed: L6, techs: ['prompts1', 'benchspeed', 'canary', 'quarantine', 'autoreset', 'range', 'refactor', 'racks', 'belts', 'swarm', 'prompts2'],
  flags: { defects: true, threats: true, approvals: true, forgeNeedsSpec: true, blueprints: true, diagnoses: true },
  approvalDelay: 45, denyChance: 0.35, zoneRisk: 0.6, monolithRisk: 0.85, nestBase: 0.8, evolution: 0.2,
  goal: { type: 'ship', count: 1_000_000 }, timeLimit: 0,
  stars: [
    { kind: 'safety', text: 'Keep trust at 90 or higher', test: { type: 'trust', min: 90 } },
    { kind: 'agency', text: 'Find the terminal', test: { type: 'secret', id: 'terminal' } },
  ],
  introduces: [],
  features: [
    { t: 'reveal', x: 64, y: 48, r: 24 },
    { t: 'users', x: 63, y: 46 },
    { t: 'rack', x: 56, y: 50 },
    { t: 'patch', res: 'request', x: 40, y: 34, r: 3.2 },
    { t: 'patch', res: 'context', x: 40, y: 62, r: 3.2 },
    { t: 'patch', res: 'request', x: 88, y: 34, r: 3.2 },
    { t: 'patch', res: 'context', x: 88, y: 62, r: 3.2 },
    { t: 'monolith', x: 61, y: 10 },
    { t: 'terminal', x: 118, y: 88 },
    { t: 'nest', x: 3, y: 3 },
    { t: 'nest', x: 122, y: 3 },
    { t: 'nest', x: 3, y: 90 },
  ],
};

export function levelById(id: string): LevelDef | undefined {
  return [...LEVELS, SANDBOX].find((l) => l.id === id);
}
